import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useSubmitForm } from "../../../../hooks/useSubmitForm";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { authPasswordService } from "../../../api_client";
import { isCompleteOtp, sanitizeOtp } from "../../../../utils/validation";

const RESEND_COOLDOWN_SECONDS = 60;

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

type VerifyResetOtpFormProps = { email: string };

export function VerifyResetOtpForm({ email }: VerifyResetOtpFormProps) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [isResending, setIsResending] = useState(false);

  const { formFields, setFormFields } = useFormFields<{ otp: string }>({ otp: "" });

  const { isLoading, errors, submitHandler } = useSubmitForm<{ otp: string }>({
    submitMethod: async () =>
      authPasswordService.passwordVerifyResetOtp({ email, otp: formFields.otp }),
    runAfterSuccess: (data) => {
      showSuccessToast("OTP verified");
      navigate(AppRoutes.RESET_PASSWORD, {
        state: { token: data.tempToken, email },
        replace: true,
      });
    },
    runAfterError: (error) => {
      showErrorToast("Invalid or expired OTP", error);
    },
  });

  useEffect(() => {
    if (!isResendDisabled) return;
    if (countdown <= 0) { setIsResendDisabled(false); return; }
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown, isResendDisabled]);

  const onResend = async () => {
    if (isResendDisabled || isResending) return;
    setIsResending(true);
    try {
      await authPasswordService.passwordForgot({ email });
      showSuccessToast("Reset code resent");
      setCountdown(RESEND_COOLDOWN_SECONDS);
      setIsResendDisabled(true);
    } catch (error) {
      showErrorToast("Failed to resend code", error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form onSubmit={submitHandler} className="flex w-full flex-col gap-10">
      <Input
        label="Security code"
        type="text"
        name="otp"
        placeholder="Enter Security Code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={formFields.otp}
        onChange={(e) => setFormFields({ otp: sanitizeOtp(e.target.value) })}
        error={errors?.otp?.[0]}
      />

      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-1.5">
          <Text variant="bodySm" tone="muted" inline>Didn't receive it?</Text>
          <button
            type="button"
            onClick={onResend}
            disabled={isResendDisabled || isResending}
            className="disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Text variant="bodySmBold" tone="inherit" inline className="!text-accentCyan700">
              Resend OTP
            </Text>
          </button>
        </div>
        {isResendDisabled && (
          <Text variant="bodySmBold" tone="inherit" className="!text-accentCyan700">
            {formatCountdown(countdown)}
          </Text>
        )}
      </div>

      <Button variant="primary" fullWidth type="submit" disabled={isLoading || !isCompleteOtp(formFields.otp)}>
        {isLoading ? "Verifying…" : "Continue"}
      </Button>
    </form>
  );
}

export default VerifyResetOtpForm;
